// ✅ src/hooks/usePrayerActions.ts (final no-reload version)
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { usePrayerStore } from './usePrayerStore';
import { useAuth } from '@/contexts/auth';
import { useRef } from 'react';

export function usePrayerActions() {
  const {
    setUserPrayers,
    setCommunityPrayers,
    userPrayers,
    communityPrayers,
  } = usePrayerStore();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();

  // snapshot for rollback on failure
  const currentStateRef = useRef({ userPrayers, communityPrayers });
  currentStateRef.current = { userPrayers, communityPrayers };

  const toggleFavorite = async (id: string): Promise<boolean> => {
    if (authLoading) return false;
    if (!isAuthenticated || !user) {
      toast.error('Please sign in to favorite prayers.');
      return false;
    }

    const userId = user.id;
    const { userPrayers: prevUser, communityPrayers: prevComm } = currentStateRef.current;

    // 🔄 Optimistic update only
    setUserPrayers(prev =>
      prev.map(p =>
        p.id === id ? { ...p, isFavorite: !p.isFavorite, _optimistic: true } : p
      )
    );
    setCommunityPrayers(prev =>
      prev.map(p =>
        p.id === id ? { ...p, isFavorite: !p.isFavorite, _optimistic: true } : p
      )
    );

    try {
      // Check if favorite already exists
      const { data: existing } = await supabase
        .from('prayer_favorites')
        .select('*')
        .eq('prayer_id', id)
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('prayer_favorites')
          .delete()
          .eq('prayer_id', id)
          .eq('user_id', userId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('prayer_favorites')
          .insert({ prayer_id: id, user_id: userId });
        if (error) throw error;
      }

      // ✅ No page reload
      toast.success('Favorite updated');
      return true;
    } catch (err: any) {
      console.error('❌ toggleFavorite error:', err);
      toast.error('Could not update favorite. Please try again.');
      // rollback
      setUserPrayers(prevUser);
      setCommunityPrayers(prevComm);
      return false;
    }
  };

  const toggleBookmark = async (id: string): Promise<boolean> => {
    if (authLoading) return false;
    if (!isAuthenticated || !user) {
      toast.error('Please sign in to bookmark prayers.');
      return false;
    }

    const userId = user.id;
    const { userPrayers: prevUser, communityPrayers: prevComm } = currentStateRef.current;

    // 🔄 Optimistic update only
    setUserPrayers(prev =>
      prev.map(p =>
        p.id === id ? { ...p, isBookmarked: !p.isBookmarked, _optimistic: true } : p
      )
    );
    setCommunityPrayers(prev =>
      prev.map(p =>
        p.id === id ? { ...p, isBookmarked: !p.isBookmarked, _optimistic: true } : p
      )
    );

    try {
      const { data: existing } = await supabase
        .from('prayer_bookmarks')
        .select('*')
        .eq('prayer_id', id)
        .eq('user_id', userId)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('prayer_bookmarks')
          .delete()
          .eq('prayer_id', id)
          .eq('user_id', userId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('prayer_bookmarks')
          .insert({ prayer_id: id, user_id: userId });
        if (error) throw error;
      }

      // ✅ No global reload
      toast.success('Bookmark updated');
      return true;
    } catch (err: any) {
      console.error('❌ toggleBookmark error:', err);
      toast.error('Could not update bookmark. Please try again.');
      // rollback
      setUserPrayers(prevUser);
      setCommunityPrayers(prevComm);
      return false;
    }
  };

  return { toggleFavorite, toggleBookmark };
}
